import React, { useCallback, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { API_KEY, LIVE_API_BASE_URL, getApiHeaders } from '../config/api';

const liveApiBase = `${LIVE_API_BASE_URL.replace(/\/$/, '')}/v1/live`;

type PresignResponse = {
  backgroundId: string;
  uploadUrl: string;
  fileKey: string;
  fileUrl: string;
};

const CreateLiveBackground: React.FC = () => {
  const navigate = useNavigate();
  const fileRef = useRef<HTMLInputElement>(null);

  const [name, setName] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState('');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [presignResult, setPresignResult] = useState<PresignResponse | null>(null);

  const headerWithAuth = useCallback(() => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      toast.error('No token found. Please log in.');
      return null;
    }
    if (!API_KEY) {
      toast.error('Missing API key. Set REACT_APP_API_KEY in your .env file.');
      return null;
    }
    return getApiHeaders(token);
  }, []);

  const handleFileChange = (file: File | null) => {
    setImageFile(file);
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    if (file?.type.startsWith('image/')) {
      setImagePreview(URL.createObjectURL(file));
    } else {
      setImagePreview('');
    }
  };

  const handleUpload = async () => {
    if (!name.trim()) {
      toast.error('Background name is required.');
      return;
    }
    if (!imageFile) {
      toast.error('Select a JPEG image file.');
      return;
    }
    if (!imageFile.type.includes('jpeg') && !imageFile.type.includes('jpg')) {
      toast.error('Only JPEG images are supported.');
      return;
    }

    const headers = headerWithAuth();
    if (!headers) return;

    setUploading(true);
    try {
      const presignRes = await fetch(`${liveApiBase}/backgrounds/presign`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ name: name.trim() }),
      });
      const presignJson = await presignRes.json();
      if (!presignRes.ok) throw new Error(presignJson.message || 'Failed to get upload URL');
      const presign = presignJson.data as PresignResponse;

      const imageBuffer = await imageFile.arrayBuffer();
      const uploadRes = await fetch(presign.uploadUrl, {
        method: 'PUT',
        body: imageBuffer,
        headers: { 'Content-Type': 'image/jpeg' },
      });
      if (!uploadRes.ok) throw new Error('Failed to upload image');

      setPresignResult(presign);
      toast.success('Image uploaded — save the background to complete.');
    } catch (error) {
      toast.error((error as Error).message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!presignResult) {
      toast.error('Upload the image first.');
      return;
    }

    const headers = headerWithAuth();
    if (!headers) return;

    setSaving(true);
    try {
      const res = await fetch(`${liveApiBase}/backgrounds`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          backgroundId: presignResult.backgroundId,
          name: name.trim(),
          fileKey: presignResult.fileKey,
        }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.message || 'Failed to save background');
      toast.success(json.message || 'Background saved');
      navigate('/live-backgrounds');
    } catch (error) {
      toast.error((error as Error).message || 'Failed to save background');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className='min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900'>
      <Navbar />

      <div className='max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-6'>
        <div className='flex items-center justify-between'>
          <div>
            <h1 className='text-2xl sm:text-3xl font-bold text-white'>Upload Live Background</h1>
            <p className='text-sm text-gray-400'>Upload a JPEG image to use as a livestream background.</p>
          </div>
          <button
            onClick={() => navigate('/live-backgrounds')}
            className='px-4 py-2 text-sm font-semibold rounded-lg bg-white/10 text-white hover:bg-white/20 transition'
          >
            Back to Backgrounds
          </button>
        </div>

        <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
          {/* Step 1 — Upload */}
          <div className='bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 space-y-4'>
            <div>
              <p className='text-xs uppercase tracking-[0.2em] text-gray-500 mb-2'>Step 1</p>
              <h2 className='text-lg font-semibold text-white'>Upload Image</h2>
              <p className='text-xs text-gray-400 mt-1'>JPEG only. This image will be used as a live background.</p>
            </div>

            <div>
              <label className='block text-xs text-gray-400 mb-1'>Background Name *</label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className='w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#FF9C15]/60'
                placeholder='e.g. Studio Gradient'
              />
            </div>

            <div>
              <label className='block text-xs text-gray-400 mb-1'>JPEG Image *</label>
              <input
                ref={fileRef}
                type='file'
                accept='image/jpeg,image/jpg'
                onChange={(e) => handleFileChange(e.target.files?.[0] || null)}
                className='w-full text-sm text-gray-300'
              />
              {imageFile && (
                <div className='mt-1 text-xs text-gray-400'>
                  {imageFile.name} ({(imageFile.size / 1024).toFixed(1)} KB)
                </div>
              )}
              {imagePreview && (
                <img
                  src={imagePreview}
                  alt='Preview'
                  className='mt-3 w-full h-40 object-cover rounded-lg border border-white/10'
                />
              )}
            </div>

            <button
              onClick={handleUpload}
              disabled={uploading || !!presignResult}
              className='w-full py-2.5 bg-gradient-to-r from-[#FF9C15] to-[#F42026] text-white font-semibold rounded-lg shadow-md hover:shadow-lg transition-all disabled:opacity-60'
            >
              {uploading ? 'Uploading...' : presignResult ? 'Uploaded' : 'Upload Image'}
            </button>

            {presignResult && (
              <div className='bg-white/5 border border-white/10 rounded-lg p-3 text-xs text-gray-300 space-y-1'>
                <div className='text-emerald-300 font-semibold'>Image uploaded successfully</div>
                <div className='truncate'>Key: {presignResult.fileKey}</div>
                <a
                  href={presignResult.fileUrl}
                  target='_blank'
                  rel='noreferrer'
                  className='text-[#FF9C15] hover:underline break-all block'
                >
                  {presignResult.fileUrl}
                </a>
              </div>
            )}
          </div>

          {/* Step 2 — Save */}
          <div className='bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 space-y-4'>
            <div>
              <p className='text-xs uppercase tracking-[0.2em] text-gray-500 mb-2'>Step 2</p>
              <h2 className='text-lg font-semibold text-white'>Save Background</h2>
              <p className='text-xs text-gray-400 mt-1'>Persist the background record in the database.</p>
            </div>

            <div className='bg-black/20 border border-white/10 rounded-lg p-4 space-y-2 text-xs text-gray-400'>
              <div className='flex justify-between'>
                <span>Name</span>
                <span className='text-white'>{name || '—'}</span>
              </div>
              <div className='flex justify-between'>
                <span>Background ID</span>
                <span className='text-white font-mono'>{presignResult?.backgroundId || '—'}</span>
              </div>
              <div className='flex justify-between'>
                <span>File</span>
                <span className='text-white'>{imageFile?.name || '—'}</span>
              </div>
              <div className='flex justify-between'>
                <span>Uploaded</span>
                <span className={presignResult ? 'text-emerald-300' : 'text-gray-500'}>
                  {presignResult ? 'Yes' : 'No'}
                </span>
              </div>
            </div>

            <button
              onClick={handleSave}
              disabled={saving || !presignResult}
              className='w-full py-2.5 bg-gradient-to-r from-[#FF9C15] to-[#F42026] text-white font-semibold rounded-lg shadow-md hover:shadow-lg transition-all disabled:opacity-60'
            >
              {saving ? 'Saving...' : 'Save Background'}
            </button>

            {!presignResult && (
              <p className='text-xs text-gray-500 text-center'>Complete Step 1 first.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateLiveBackground;
